import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Tabs, Tab, CircularProgress,
  Alert, Select, MenuItem, FormControl, InputLabel, Chip, LinearProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper, Divider
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Grade as GradeIcon,
  EmojiEvents as LaureatIcon,
  Campaign as ReclamIcon,
  Badge as HRIcon,
  Storage as SystemIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  PhotoCamera as PhotoIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import { adminAPI } from '../../services/api';

// ─── Color palette ────────────────────────────────────────────────────────────
const COLORS = ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#e91e63'];
const GENDER_COLORS = { M: '#3498db', F: '#e91e63', 'N/A': '#95a5a6' };

// ─── Reusable KPI card ────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon, color = '#3498db', chip }) => (
  <Card sx={{ height: '100%', borderTop: `4px solid ${color}`, borderRadius: 3 }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
          <Typography variant="h4" fontWeight="700" sx={{ color }}>{value?.toLocaleString?.() ?? value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          {chip && <Box sx={{ mt: 1 }}><Chip label={chip} size="small" sx={{ bgcolor: `${color}20`, color }} /></Box>}
        </Box>
        <Box sx={{ bgcolor: `${color}15`, borderRadius: 2, p: 1.5, color }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Section title ────────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 2, color: '#2c3e50' }}>
    {children}
  </Typography>
);

// ─── Chart wrapper ────────────────────────────────────────────────────────────
const ChartCard = ({ title, children, height = 300 }) => (
  <Card sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent>
      {title && <Typography variant="subtitle1" fontWeight="600" gutterBottom>{title}</Typography>}
      <Box sx={{ height }}>{children}</Box>
    </CardContent>
  </Card>
);

// ─── Custom tooltip for recharts ──────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={3} sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="caption" fontWeight="600">{label}</Typography>
      {payload.map((p, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </Typography>
      ))}
    </Paper>
  );
};

// ─── Tab panel helper ─────────────────────────────────────────────────────────
const TabPanel = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const StatisticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [year, setYear] = useState('');

  const load = useCallback(async (selectedYear) => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminAPI.getStatistics(selectedYear || undefined);
      setData(result);
      if (!selectedYear) setYear(result.current_year);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleYearChange = (e) => {
    setYear(e.target.value);
    load(e.target.value);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <CircularProgress size={60} />
    </Box>
  );

  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  if (!data) return null;

  const { students, grades, laureats, reclamations, employees, system, diploma_path, available_years } = data;

  // ── helpers ──────────────────────────────────────────────────────────────────
  const pct = (a, b) => b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '0%';

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" color="#2c3e50">Tableau de Bord Statistiques</Typography>
          <Typography variant="body2" color="text.secondary">Données analytiques complètes de l'établissement</Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Année académique</InputLabel>
          <Select value={year} label="Année académique" onChange={handleYearChange}>
            {(available_years || []).map(y => (
              <MenuItem key={y} value={y}>{y - 1}/{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── Global KPIs ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Étudiants" value={students.total} icon={<PeopleIcon />} color="#3498db" sub={`${students.new_students} nouveaux`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Notes saisies" value={grades.total} icon={<GradeIcon />} color="#2ecc71" sub={`Moy. ${grades.average}`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Taux de réussite" value={`${grades.success_rate}%`} icon={<CheckIcon />} color="#27ae60" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Lauréats" value={laureats.total} icon={<LaureatIcon />} color="#9b59b6" />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Réclamations" value={reclamations.total} icon={<ReclamIcon />} color="#e67e22" sub={`${reclamations.pending} en attente`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Employés" value={employees.total} icon={<HRIcon />} color="#e74c3c" sub={`${employees.professors} prof.`} />
        </Grid>
      </Grid>

      {/* ── Tabs ── */}
      <Card sx={{ borderRadius: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Vue Générale" />
          <Tab label="Étudiants" />
          <Tab label="Résultats Académiques" />
          <Tab label="Lauréats" />
          <Tab label="Réclamations" />
          <Tab label="Ressources Humaines" />
          <Tab label="Système" />
          <Tab label="مسار الديبلوم" />
        </Tabs>

        <Box sx={{ p: 3 }}>

          {/* ══════════════════════════════════════════════════════ TAB 0 — VUE GÉNÉRALE */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={3}>
              {/* Students evolution */}
              <Grid item xs={12} md={8}>
                <ChartCard title="Évolution du nombre d'étudiants" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={students.by_year} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3498db" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3498db" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="count" name="Étudiants" stroke="#3498db" fill="url(#gradBlue)" strokeWidth={2} dot={{ r: 3 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Gender pie */}
              <Grid item xs={12} md={4}>
                <ChartCard title="Répartition par genre" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={students.by_gender} dataKey="count" nameKey="gender" cx="50%" cy="50%" outerRadius={90} label={({ gender, percent }) => `${gender} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {students.by_gender.map((entry, i) => (
                          <Cell key={i} fill={GENDER_COLORS[entry.gender] || COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Grades trend */}
              <Grid item xs={12} md={8}>
                <ChartCard title="Évolution du taux de réussite" height={260}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={grades.trend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="success_rate" name="Taux réussite %" stroke="#2ecc71" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="average" name="Moyenne" stroke="#3498db" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Quick summary */}
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>Résumé de l'année {year - 1}/{year}</Typography>
                    <Divider sx={{ mb: 2 }} />
                    {[
                      { label: 'Nouveaux étudiants', value: students.new_students, color: '#2ecc71' },
                      { label: 'Redoublants', value: students.repeaters, color: '#e74c3c' },
                      { label: 'Première inscription cycle', value: students.first_year, color: '#3498db' },
                      { label: 'Avec photo', value: students.with_photo, color: '#9b59b6' },
                      { label: 'Avec CIN', value: students.with_cin, color: '#e67e22' },
                      { label: 'Examens planifiés', value: system.exam_count, color: '#1abc9c' },
                    ].map(({ label, value, color }) => (
                      <Box key={label} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">{label}</Typography>
                          <Typography variant="caption" fontWeight="600" sx={{ color }}>{value?.toLocaleString()}</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.min(100, (value / students.total) * 100)} sx={{ height: 5, borderRadius: 3, bgcolor: `${color}20`, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 1 — ÉTUDIANTS */}
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}><KpiCard label="Total étudiants" value={students.total} icon={<PeopleIcon />} color="#3498db" /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Nouveaux" value={students.new_students} icon={<TrendingUpIcon />} color="#2ecc71" chip={pct(students.new_students, students.total)} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Redoublants" value={students.repeaters} icon={<TrendingDownIcon />} color="#e74c3c" chip={pct(students.repeaters, students.total)} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Couverture photo" value={`${system.photo_coverage}%`} icon={<PhotoIcon />} color="#9b59b6" sub={`${students.with_photo} / ${students.total}`} /></Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* By program bar */}
              <Grid item xs={12} md={7}>
                <ChartCard title="Étudiants par filière (Top 15)" height={380}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={students.by_program} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="lib_etp" width={160} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Étudiants" fill="#3498db" radius={[0, 4, 4, 0]}>
                        {students.by_program.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={5}>
                <Grid container spacing={2}>
                  {/* Gender */}
                  <Grid item xs={12}>
                    <ChartCard title="Répartition par genre" height={200}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={students.by_gender} dataKey="count" nameKey="gender" cx="50%" cy="50%" outerRadius={75} label={({ gender, count, percent }) => `${gender}: ${count} (${(percent * 100).toFixed(0)}%)`}>
                            {students.by_gender.map((entry, i) => <Cell key={i} fill={GENDER_COLORS[entry.gender] || COLORS[i]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </Grid>

                  {/* New vs repeater */}
                  <Grid item xs={12}>
                    <ChartCard title="Nouveaux vs Redoublants" height={170}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={[{ name: `${year-1}/${year}`, Nouveaux: students.new_students, Redoublants: students.repeaters }]} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="Nouveaux" fill="#2ecc71" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Redoublants" fill="#e74c3c" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </Grid>
                </Grid>
              </Grid>

              {/* Evolution */}
              <Grid item xs={12}>
                <ChartCard title="Évolution des inscriptions par année académique" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={students.by_year} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="gradStudent" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3498db" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3498db" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="count" name="Étudiants inscrits" stroke="#3498db" fill="url(#gradStudent)" strokeWidth={2} dot={{ r: 4 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 2 — RÉSULTATS */}
          <TabPanel value={tab} index={2}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={2}><KpiCard label="Total notes" value={grades.total} icon={<GradeIcon />} color="#2ecc71" /></Grid>
              <Grid item xs={6} md={2}><KpiCard label="Moyenne générale" value={grades.average} icon={<SchoolIcon />} color="#3498db" /></Grid>
              <Grid item xs={6} md={2}><KpiCard label="Taux de réussite" value={`${grades.success_rate}%`} icon={<CheckIcon />} color="#27ae60" /></Grid>
              <Grid item xs={6} md={2}><KpiCard label="Admis" value={grades.passed} icon={<CheckIcon />} color="#1abc9c" chip={pct(grades.passed, grades.total)} /></Grid>
              <Grid item xs={6} md={2}><KpiCard label="Echecs" value={grades.failed} icon={<TrendingDownIcon />} color="#e74c3c" chip={pct(grades.failed, grades.total)} /></Grid>
              <Grid item xs={6} md={2}><KpiCard label="Note min / max" value={`${grades.min} / ${grades.max}`} icon={<GradeIcon />} color="#9b59b6" /></Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* Distribution */}
              <Grid item xs={12} md={6}>
                <ChartCard title="Distribution des notes" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={grades.distribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Nombre de notes" radius={[4, 4, 0, 0]}>
                        {grades.distribution.map((entry, i) => {
                          const rangeStart = parseInt(entry.range.split('-')[0]);
                          const color = rangeStart < 8 ? '#e74c3c' : rangeStart < 10 ? '#e67e22' : rangeStart < 14 ? '#f39c12' : '#2ecc71';
                          return <Cell key={i} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* By session */}
              <Grid item xs={12} md={6}>
                <ChartCard title="Résultats par session" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={grades.by_session} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="session" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="total" name="Total notes" fill="#3498db" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="passed" name="Admis" fill="#2ecc71" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="success_rate" name="Taux %" stroke="#e74c3c" strokeWidth={2} dot={{ r: 5 }} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Trend */}
              <Grid item xs={12}>
                <ChartCard title="Évolution des résultats académiques" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={grades.trend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="total" name="Total notes" fill="#eaf4fc" stroke="#3498db" />
                      <Line yAxisId="left" type="monotone" dataKey="average" name="Moyenne" stroke="#3498db" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="right" type="monotone" dataKey="success_rate" name="Taux réussite %" stroke="#2ecc71" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* By program */}
              <Grid item xs={12} md={8}>
                <ChartCard title="Résultats par filière" height={320}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={grades.by_program} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="lib_etp" width={150} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="success_rate" name="Taux réussite %" fill="#2ecc71" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="average" name="Moyenne /20" fill="#3498db" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Difficult + best modules */}
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="600" color="error" gutterBottom>
                      🔴 Modules les plus difficiles
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>Module</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>Moy.</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>Taux</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {grades.difficult_modules.map((m, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.lib_elp}>{m.lib_elp}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 10, color: '#e74c3c', fontWeight: 600 }}>{m.average}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 10 }}>{m.pass_rate}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="600" color="success.main" gutterBottom>
                      🟢 Meilleurs modules
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: 11, fontWeight: 600 }}>Module</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>Moy.</TableCell>
                          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600 }}>Taux</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {grades.best_modules.map((m, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.lib_elp}>{m.lib_elp}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 10, color: '#27ae60', fontWeight: 600 }}>{m.average}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 10 }}>{m.pass_rate}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 3 — LAURÉATS */}
          <TabPanel value={tab} index={3}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={4}><KpiCard label="Total lauréats" value={laureats.total} icon={<LaureatIcon />} color="#9b59b6" /></Grid>
              <Grid item xs={6} md={4}><KpiCard label="Multi-diplômés" value={laureats.multi_diploma} icon={<SchoolIcon />} color="#8e44ad" chip={pct(laureats.multi_diploma, laureats.total)} /></Grid>
              <Grid item xs={6} md={4}><KpiCard label="Types de diplômes" value={laureats.by_diploma.length} icon={<SchoolIcon />} color="#6c3483" /></Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* By year */}
              <Grid item xs={12} md={7}>
                <ChartCard title="Lauréats par année" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={laureats.by_year} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="count" name="Lauréats" fill="#9b59b6" radius={[4, 4, 0, 0]}>
                        {laureats.by_year.map((_, i) => <Cell key={i} fill={`hsl(${280 + i * 5}, 60%, ${55 + (i % 3) * 5}%)`} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* By diploma pie */}
              <Grid item xs={12} md={5}>
                <ChartCard title="Répartition par diplôme" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={laureats.by_diploma} dataKey="count" nameKey="diploma" cx="50%" cy="50%" outerRadius={100} label={({ diploma, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine>
                        {laureats.by_diploma.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} />
                      <Legend formatter={(value) => value?.length > 20 ? value.substring(0, 20) + '…' : value} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Diploma table */}
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>Détail par type de diplôme</Typography>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                          <TableCell fontWeight="600">Diplôme</TableCell>
                          <TableCell align="right">Nombre</TableCell>
                          <TableCell align="right">Pourcentage</TableCell>
                          <TableCell>Progression</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {laureats.by_diploma.map((d, i) => (
                          <TableRow key={i} hover>
                            <TableCell>{d.diploma}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{d.count.toLocaleString()}</TableCell>
                            <TableCell align="right">{pct(d.count, laureats.total)}</TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                              <LinearProgress variant="determinate" value={(d.count / laureats.by_diploma[0]?.count) * 100} sx={{ height: 8, borderRadius: 4, bgcolor: `${COLORS[i % COLORS.length]}20`, '& .MuiLinearProgress-bar': { bgcolor: COLORS[i % COLORS.length] } }} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 4 — RÉCLAMATIONS */}
          <TabPanel value={tab} index={4}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}><KpiCard label="Total" value={reclamations.total} icon={<ReclamIcon />} color="#e67e22" /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="En attente" value={reclamations.pending} icon={<PendingIcon />} color="#e74c3c" chip={pct(reclamations.pending, reclamations.total)} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Résolues" value={reclamations.resolved} icon={<CheckIcon />} color="#2ecc71" chip={pct(reclamations.resolved, reclamations.total)} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Taux de réponse" value={`${reclamations.response_rate}%`} icon={<TrendingUpIcon />} color="#3498db" /></Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* Status pie */}
              <Grid item xs={12} md={5}>
                <ChartCard title="Répartition par statut" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'En attente', value: reclamations.pending },
                          { name: 'En cours', value: reclamations.in_progress },
                          { name: 'Résolues', value: reclamations.resolved },
                          { name: 'Rejetées', value: reclamations.rejected },
                        ].filter(d => d.value > 0)}
                        dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {['#e74c3c', '#f39c12', '#2ecc71', '#95a5a6'].map((color, i) => <Cell key={i} fill={color} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* By type */}
              <Grid item xs={12} md={7}>
                <ChartCard title="Réclamations par type" height={280}>
                  {reclamations.by_type.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reclamations.by_type} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="type" width={120} tick={{ fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Nombre" fill="#e67e22" radius={[0, 4, 4, 0]}>
                          {reclamations.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">Aucune donnée disponible</Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>

              {/* By urgency */}
              {reclamations.by_urgency.length > 0 && (
                <Grid item xs={12}>
                  <ChartCard title="Réclamations par urgence" height={220}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={reclamations.by_urgency} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="urgency" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Nombre" radius={[4, 4, 0, 0]}>
                          {reclamations.by_urgency.map((entry, i) => {
                            const color = entry.urgency === 'HIGH' || entry.urgency === 'high' ? '#e74c3c' : entry.urgency === 'MEDIUM' || entry.urgency === 'medium' ? '#f39c12' : '#2ecc71';
                            return <Cell key={i} fill={color} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 5 — RH */}
          <TabPanel value={tab} index={5}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}><KpiCard label="Total employés" value={employees.total} icon={<HRIcon />} color="#e74c3c" /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Professeurs" value={employees.professors} icon={<SchoolIcon />} color="#3498db" chip={pct(employees.professors, employees.total)} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Personnel admin." value={employees.admin_staff} icon={<PeopleIcon />} color="#e67e22" chip={pct(employees.admin_staff, employees.total)} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Actifs" value={employees.active} icon={<CheckIcon />} color="#2ecc71" chip={pct(employees.active, employees.total)} /></Grid>
            </Grid>

            <Grid container spacing={3}>
              {/* By department */}
              <Grid item xs={12} md={7}>
                <ChartCard title="Effectifs par département" height={320}>
                  {employees.by_department.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={employees.by_department} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="dept" width={150} tick={{ fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Employés" fill="#e74c3c" radius={[0, 4, 4, 0]}>
                          {employees.by_department.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">Aucune donnée de département disponible</Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>

              {/* By gender */}
              <Grid item xs={12} md={5}>
                <ChartCard title="Répartition par genre" height={320}>
                  {employees.by_gender.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={employees.by_gender.map(e => ({ name: e.sexe === 'M' ? 'Homme' : e.sexe === 'F' ? 'Femme' : e.sexe, value: e.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                          {employees.by_gender.map((entry, i) => <Cell key={i} fill={GENDER_COLORS[entry.sexe] || COLORS[i]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">Aucune donnée disponible</Typography>
                    </Box>
                  )}
                </ChartCard>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 6 — SYSTÈME */}
          <TabPanel value={tab} index={6}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}><KpiCard label="Couverture photos" value={`${system.photo_coverage}%`} icon={<PhotoIcon />} color="#3498db" sub={`${system.photo_covered} / ${system.photo_total}`} /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Examens planifiés" value={system.exam_count} icon={<SchoolIcon />} color="#9b59b6" /></Grid>
              <Grid item xs={6} md={3}><KpiCard label="Services synchronisés" value={system.sync_status.length} icon={<SystemIcon />} color="#1abc9c" /></Grid>
              <Grid item xs={6} md={3}>
                <KpiCard
                  label="Dernière synchro"
                  value={system.sync_status[0]?.last_sync ? new Date(system.sync_status[0].last_sync).toLocaleDateString('fr-FR') : 'N/A'}
                  icon={<SystemIcon />}
                  color="#e67e22"
                />
              </Grid>
            </Grid>

            {/* Photo coverage bar */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>Couverture photographique des étudiants</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Avec photo: {system.photo_covered?.toLocaleString()}</Typography>
                  <Typography variant="body2" fontWeight="600" color="#3498db">{system.photo_coverage}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={system.photo_coverage}
                  sx={{ height: 16, borderRadius: 8, bgcolor: '#eaf4fc', '& .MuiLinearProgress-bar': { bgcolor: system.photo_coverage > 80 ? '#2ecc71' : system.photo_coverage > 50 ? '#f39c12' : '#e74c3c', borderRadius: 8 } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">0</Typography>
                  <Typography variant="caption" color="text.secondary">Total: {system.photo_total?.toLocaleString()}</Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Sync status table */}
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>État de la synchronisation Oracle → PostgreSQL</Typography>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Exécutions</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Succès</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Erreurs</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Enregistrements</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Dernière synchro</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {system.sync_status.map((s, i) => {
                      const successRate = s.runs > 0 ? ((s.successes / s.runs) * 100).toFixed(0) : 0;
                      const isHealthy = successRate >= 90;
                      return (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{s.type}</TableCell>
                          <TableCell align="right">{s.runs}</TableCell>
                          <TableCell align="right" sx={{ color: '#2ecc71', fontWeight: 600 }}>{s.successes}</TableCell>
                          <TableCell align="right" sx={{ color: s.errors > 0 ? '#e74c3c' : 'inherit', fontWeight: s.errors > 0 ? 600 : 400 }}>{s.errors}</TableCell>
                          <TableCell align="right">{parseInt(s.total_records || 0).toLocaleString()}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{s.last_sync ? new Date(s.last_sync).toLocaleString('fr-FR') : 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={isHealthy ? `${successRate}% OK` : `${successRate}% ⚠`}
                              sx={{ bgcolor: isHealthy ? '#e8f8f0' : '#fef5e7', color: isHealthy ? '#27ae60' : '#e67e22', fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabPanel>

          {/* ══════════════════════════════════════════════════════ TAB 7 — مسار الديبلوم */}
          <TabPanel value={tab} index={7}>
            {(!diploma_path?.by_semester?.length) ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                لا توجد بيانات تسجيلات بيداغوجية متاحة. تأكد من مزامنة الوضعية البيداغوجية (IND_CONTRAT_ELP).
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {/* KPIs */}
                <Grid item xs={6} md={3}>
                  <KpiCard
                    label="إجمالي الفصول"
                    value={diploma_path.by_semester.length}
                    icon={<SchoolIcon />}
                    color="#3498db"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KpiCard
                    label="إجمالي الطلاب المسجلين"
                    value={Math.max(...diploma_path.by_semester.map(s => s.student_count))}
                    icon={<PeopleIcon />}
                    color="#2ecc71"
                    sub="أعلى فصل"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KpiCard
                    label="إجمالي التسجيلات"
                    value={diploma_path.by_semester.reduce((s, r) => s + r.registration_count, 0)}
                    icon={<GradeIcon />}
                    color="#9b59b6"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <KpiCard
                    label="متوسط الوحدات / طالب / فصل"
                    value={(() => {
                      const tot = diploma_path.by_semester.reduce((s, r) => s + r.registration_count, 0);
                      const stu = diploma_path.by_semester.reduce((s, r) => s + r.student_count, 0);
                      return stu > 0 ? (tot / stu).toFixed(1) : '—';
                    })()}
                    icon={<TrendingUpIcon />}
                    color="#f39c12"
                  />
                </Grid>

                {/* Bar chart: students per semester */}
                <Grid item xs={12} md={7}>
                  <ChartCard title="عدد الطلاب المسجلين حسب الفصل" height={320}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={diploma_path.by_semester} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="semester_code" tick={{ fontSize: 12, fontWeight: 600 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="student_count" name="عدد الطلاب" radius={[6, 6, 0, 0]}>
                          {diploma_path.by_semester.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>

                {/* Bar chart: registrations per semester */}
                <Grid item xs={12} md={5}>
                  <ChartCard title="عدد التسجيلات حسب الفصل" height={320}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={diploma_path.by_semester} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="semester_code" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="registration_count" name="عدد التسجيلات" radius={[6, 6, 0, 0]}>
                          {diploma_path.by_semester.map((_, i) => (
                            <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>

                {/* Detail table */}
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                        تفصيل التسجيلات البيداغوجية حسب الفصل
                      </Typography>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 700 }}>الفصل</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>عدد الطلاب</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>عدد التسجيلات</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>متوسط الوحدات/طالب</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>التوزيع</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {diploma_path.by_semester.map((row, i) => {
                            const avg = row.student_count > 0
                              ? (row.registration_count / row.student_count).toFixed(1)
                              : '—';
                            const maxStudents = Math.max(...diploma_path.by_semester.map(s => s.student_count));
                            const barPct = maxStudents > 0 ? (row.student_count / maxStudents) * 100 : 0;
                            return (
                              <TableRow key={row.semester_code} hover>
                                <TableCell>
                                  <Chip
                                    label={row.semester_code}
                                    size="small"
                                    sx={{ bgcolor: COLORS[i % COLORS.length] + '22', color: COLORS[i % COLORS.length], fontWeight: 700 }}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {row.student_count.toLocaleString()}
                                </TableCell>
                                <TableCell align="right">
                                  {row.registration_count.toLocaleString()}
                                </TableCell>
                                <TableCell align="right">{avg}</TableCell>
                                <TableCell sx={{ minWidth: 180 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={barPct}
                                    sx={{
                                      height: 8, borderRadius: 4,
                                      bgcolor: COLORS[i % COLORS.length] + '22',
                                      '& .MuiLinearProgress-bar': { bgcolor: COLORS[i % COLORS.length], borderRadius: 4 }
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </TabPanel>

        </Box>
      </Card>
    </Box>
  );
};

export default StatisticsPage;
